import { useEffect, useRef, useCallback } from 'react'
import { useDevModeStore } from '../stores/useDevModeStore'

const INACTIVITY_TIMEOUT = 15000 // 15 seconds

export function useAutoRotate() {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wasAutoRotatingRef = useRef(false)

  // Function to reset the inactivity timer
  const resetInactivityTimer = useCallback(() => {
    const { autoRotate, setAutoRotate } = useDevModeStore.getState()

    // If auto-rotate was activated by inactivity, stop it
    if (autoRotate && wasAutoRotatingRef.current) {
      console.log('[AutoRotate] Stopping auto-rotate due to user activity')
      setAutoRotate(false)
      wasAutoRotatingRef.current = false
    }

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      console.log('[AutoRotate] Starting auto-rotate after 15 seconds of inactivity')
      // Start auto-rotate after 15 seconds of inactivity
      setAutoRotate(true)
      wasAutoRotatingRef.current = true
    }, INACTIVITY_TIMEOUT)
  }, [])

  useEffect(() => {
    console.log('[AutoRotate] Setting up inactivity detection')

    // Track user activity events
    const events = [
      'mousedown',
      'mousemove',
      'mouseup',
      'click',
      'keydown',
      'keypress',
      'keyup',
      'touchstart',
      'touchmove',
      'touchend',
      'wheel',
      'scroll',
    ]

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, resetInactivityTimer)
    })

    // Start the initial timer
    resetInactivityTimer()

    // Cleanup
    return () => {
      console.log('[AutoRotate] Cleaning up inactivity detection')
      events.forEach((event) => {
        window.removeEventListener(event, resetInactivityTimer)
      })
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [resetInactivityTimer])

  return { resetInactivityTimer }
}
